import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { Collection, getCollections } from '../services/api';

interface CollectionsContextType {
  collections: Collection[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const CollectionsContext = createContext<CollectionsContextType | undefined>(undefined);

export const useCollections = (): CollectionsContextType => {
  const ctx = useContext(CollectionsContext);
  if (!ctx) {
    throw new Error('useCollections must be used within a CollectionsProvider');
  }
  return ctx;
};

interface CollectionsProviderProps {
  children: ReactNode;
}

export const CollectionsProvider: React.FC<CollectionsProviderProps> = ({ children }) => {
  const { isAuthenticated, user, getValidToken } = useAuth();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const inFlight = useRef<Promise<Collection[]> | null>(null);
  const loadedFor = useRef<string | null>(null);
  const hasLoaded = useRef(false);

  const fetchCollections = useCallback(async (): Promise<Collection[]> => {
    if (!isAuthenticated || !user?.sub) {
      throw new Error('Not authenticated');
    }
    if (inFlight.current) {
      return inFlight.current;
    }
    const promise = (async () => {
      const token = await getValidToken();
      if (!token) {
        throw new Error('Authentication required');
      }
      const result = await getCollections(token);
      return result;
    })();
    inFlight.current = promise;
    try {
      const result = await promise;
      return result;
    } finally {
      inFlight.current = null;
    }
  }, [isAuthenticated, user, getValidToken]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!isAuthenticated || !user?.sub) {
      setLoading(false);
      loadedFor.current = null;
      hasLoaded.current = false;
      return;
    }
    try {
      setError(null);
      if (!hasLoaded.current) {
        setLoading(true);
      }
      const result = await fetchCollections();
      setCollections(result);
      loadedFor.current = user.sub;
      hasLoaded.current = true;
    } catch (err) {
      console.error('CollectionsContext: failed to fetch', err);
      setError('Failed to load collections');
      // Preserve cache on error.
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user, fetchCollections]);

  useEffect(() => {
    if (!isAuthenticated || !user?.sub) {
      setLoading(false);
      loadedFor.current = null;
      hasLoaded.current = false;
      return;
    }
    if (loadedFor.current === user.sub) {
      return;
    }
    refresh();
  }, [isAuthenticated, user?.sub, refresh]);

  const value: CollectionsContextType = {
    collections,
    loading,
    error,
    refresh,
  };

  return <CollectionsContext.Provider value={value}>{children}</CollectionsContext.Provider>;
};
